import { useQuery, useQueries } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { trackEvent } from "../analytics";
import { LeagueStandingsTable } from "../components/LeagueStandingsTable";
import { ErrorBox, Loading } from "../components/Loading";

export function LeaguesPage() {
  const [year, setYear] = useState<number | null>(null);

  const yearsQuery = useQuery({
    queryKey: ["leagueYears"],
    queryFn: api.leagueYears,
  });

  useEffect(() => {
    if (year === null && yearsQuery.data && yearsQuery.data.length > 0) {
      setYear(yearsQuery.data[0]!);
    }
  }, [year, yearsQuery.data]);

  const leaguesQuery = useQuery({
    queryKey: ["leagues", year],
    queryFn: () => api.leagues(year ?? undefined),
    enabled: year !== null,
  });

  const standings = useQueries({
    queries: (leaguesQuery.data ?? []).map((l) => ({
      queryKey: ["standings", l.id],
      queryFn: () => api.leagueStandings(l.id),
    })),
  });

  if (yearsQuery.isLoading) return <Loading />;
  if (yearsQuery.error) return <ErrorBox error={yearsQuery.error} />;

  const years = yearsQuery.data ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
            Temporada
          </div>
          <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
            Ranking {year ?? ""}
          </h1>
        </div>

        {years.length > 0 && (
          <div className="flex gap-1.5 bg-white border border-line rounded-xl p-1.5">
            {years.map((y) => {
              const active = y === year;
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setYear(y);
                    trackEvent("leagues_year_change", { year: y });
                  }}
                  className={[
                    "px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                    active
                      ? "bg-brand-700 text-white"
                      : "text-[#6b6356] hover:bg-[#f3efe6]",
                  ].join(" ")}
                >
                  {y}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <p className="max-w-[600px] text-[15px] leading-relaxed text-[#6b6356]">
        Pontuação acumulada por evento ao longo da temporada. As três primeiras
        posições de cada liga destacam o pódio.
      </p>

      {leaguesQuery.isLoading && <Loading />}
      {leaguesQuery.error && <ErrorBox error={leaguesQuery.error} />}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {standings.map((q, i) => {
          const league = leaguesQuery.data?.[i];
          if (!league) return null;
          if (q.isLoading) {
            return (
              <div
                key={league.id}
                className="rounded-2xl border border-line bg-white p-6"
              >
                <Loading label={`Carregando ${league.name}...`} />
              </div>
            );
          }
          if (q.error) return <ErrorBox key={league.id} error={q.error} />;
          if (!q.data) return null;
          return <LeagueStandingsTable key={league.id} standings={q.data} />;
        })}
      </div>

      {!leaguesQuery.isLoading && leaguesQuery.data?.length === 0 && (
        <p className="text-slate-500">Sem ligas registradas para este ano.</p>
      )}
    </div>
  );
}
